import {Fragment} from 'react/jsx-runtime';
import styled from 'styled-components';
import {BasicAnalysisResult} from '@/map/@types';
import {ANAYSIS_RESULT_LABELS} from '@/map/constants';
import {AreaAnalyticsFilter, BuildingType} from '@/map/enums';
import {getAreahouseReport, getHouseMarkerInfo, getSidoName} from '@/map/utils';
import {Flexbox, FlexFullGrow, PaddingBox, Section, VerticalDivider} from '@/styled-components';
import {palette} from '@/styles/palette';
import {withComma} from '@/utils';
import {Box, ColorCircle, EmptyBox, SubText} from '../StyledComponents';
import {TotalAvgItem} from './TotalAvgItem';

interface IProps {
  data?: BasicAnalysisResult;
  isLoading?: boolean;
}

export const Household = ({data, isLoading}: IProps) => {
  if (isLoading) {
    return (
      <Root>
        <TotalAvgItem
          title="배후세대"
          style={{flexGrow: 1}}
          loading={isLoading}
          className="flex-basis-0"
        />
      </Root>
    );
  }

  const report = getAreahouseReport(data);
  const house = report?.house;
  const isEmpty =
    !isLoading && (!house || !(house.total || house.upcomingAparts));

  if (isEmpty) {
    return (
      <EmptyBox>
        배후 세대
        <br />
        정보가 없습니다
      </EmptyBox>
    );
  }

  const ctprvn = getSidoName(data?.average?.sido);
  const avg = data?.average?.[AreaAnalyticsFilter.HOUSE];

  if (!house) {
    return null;
  }

  return (
    <Root>
      <TotalAvgItem
        title="배후세대"
        unit="세대"
        total={house.total}
        sidoAvg={avg}
        sidoName={ctprvn}
        style={{flexGrow: 1}}
        loading={isLoading}
        className="flex-basis-0"
      />
      {!isLoading && (
        <>
          <PaddingBox left={6} right={8}>
            <VerticalDivider />
          </PaddingBox>
          <FlexFullGrow className="flex-basis-0">
            {Object.entries(house)
              .filter(([key, value]) => key !== 'total' && value > 0)
              .map(([key, value], idx) => {
                const markerInfo = getHouseMarkerInfo(key);

                if (key === AreaAnalyticsFilter.UPCOMMING_APTS) {
                  if (!house.APT) {
                    return (
                      <Section spacing={0} key={key}>
                        <Flexbox>
                          <ColorCircle color={markerInfo?.color} />
                          <SubText color={palette.disabled}>
                            아파트 입주 예정{' '}
                          </SubText>
                          <FlexFullGrow />
                          <SubText color={palette.disabled}>
                            {withComma(value)}세대
                          </SubText>
                        </Flexbox>
                      </Section>
                    );
                  } else {
                    return null;
                  }
                }

                return (
                  <Fragment key={key}>
                    <Section spacing={idx ? 1 : 0}>
                      <Flexbox>
                        <ColorCircle color={markerInfo?.color} />
                        <SubText>{ANAYSIS_RESULT_LABELS[key]} </SubText>
                        <FlexFullGrow />
                        <SubText>
                          <strong>{withComma(value)}세대</strong>
                        </SubText>
                      </Flexbox>
                    </Section>
                    {key === BuildingType.Apartment &&
                      !!house.upcomingAparts && (
                        <Section spacing={0}>
                          <Flexbox>
                            <ColorCircle />
                            <SubText color={palette.disabled}>
                              입주 예정{' '}
                            </SubText>
                            <FlexFullGrow />
                            <SubText color={palette.disabled}>
                              {withComma(house.upcomingAparts)}세대
                            </SubText>
                          </Flexbox>
                        </Section>
                      )}
                  </Fragment>
                );
              })}
          </FlexFullGrow>
        </>
      )}
    </Root>
  );
};

const Root = styled(Box)`
  display: flex;
  padding: 4px;
  .flex-basis-0 {
    flex-basis: 0;
  }
`;
